'use client';
import { useState } from 'react';
import Image from 'next/image';

import CloseButton from '@/src/components/UI/CloseButton';
import { TeamMember } from '@/src/gql/graphql';

import Heading from '../Typography/Heading';
import Paragraph from '../Typography/Paragraph';

interface TeamMemberCardProps {
  member: TeamMember;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member }) => {
  if (!member) return null;
  const [showBio, setShowBio] = useState<string | boolean>(false);

  return (
    <>
      {showBio && (
        <div
          className="flex fixed h-full w-full lg:items-center justify-center z-50 p-8 top-0 left-0"
          aria-modal
        >
          <div
            className="bg-black/25 absolute top-0 left-0 w-full h-full"
            onClick={() => setShowBio(false)}
          />
          <div className="w-[600px] max-w-full overflow-scroll p-16 border-2 border-black rounded-theme bg-white relative">
            <div className="absolute top-4 right-4">
              <CloseButton onClick={() => setShowBio(false)} />
            </div>
            <Paragraph html text={showBio as string} />
          </div>
        </div>
      )}
      <article className="bg-blue border-2 border-black rounded-theme p-8 gap-6 col-span-1 md:col-span-6 flex flex-col justify-between md:flex-row md:items-start ">
        <div>
          <div>
            <Heading level="h3" text={member.title!} style="!mb-2" />
            {member.teamMemberFields?.teamMemberGroup?.title && (
              <Paragraph
                style="font-bold mb-2"
                text={member.teamMemberFields.teamMemberGroup.title}
              />
            )}
            {member.teamMemberFields?.teamMemberGroup?.location && (
              <Paragraph
                style="mb-4"
                text={member.teamMemberFields?.teamMemberGroup.location}
              />
            )}

            {member.teamMemberFields?.teamMemberGroup?.quote && (
              <Paragraph
                html
                style="mb-4"
                text={member.teamMemberFields.teamMemberGroup.quote}
              />
            )}
            {member.teamMemberFields?.teamMemberGroup?.quoteAuthor && (
              <Paragraph
                style="!mb-4"
                text={
                  '- ' + member.teamMemberFields.teamMemberGroup.quoteAuthor
                }
              />
            )}
          </div>
          {member.teamMemberFields?.teamMemberGroup?.bio && (
            <div>
              <p className="text-sm">
                <a
                  aria-haspopup="dialog"
                  aria-label={`Read ${member.title!}'s bio`}
                  className="cursor-pointer"
                  title={`Click to read ${member.title!}'s bio`}
                  onClick={() => {
                    const bio = member.teamMemberFields?.teamMemberGroup?.bio;
                    if (typeof bio === 'string') {
                      setShowBio(bio);
                    }
                  }}
                  role="button"
                >
                  Read bio
                </a>
              </p>
            </div>
          )}
        </div>
        {member.teamMemberFields?.teamMemberGroup?.image?.node?.sourceUrl && (
          <div>
            <div className="w-[240px] mx-auto">
              <Image
                alt={member.title!}
                className="w-[240px] h-[240px] object-cover relative overflow-hidden rounded-theme border-2 border-black"
                height={240}
                width={240}
                src={
                  member.teamMemberFields.teamMemberGroup.image.node.sourceUrl
                }
              />
            </div>
          </div>
        )}
      </article>
    </>
  );
};

export default TeamMemberCard;
